import clsx from 'clsx';
import React from 'react';

/**
 * Buttons: three visible weights + destructive.
 * - primary   → one per view. The action that moves work forward.
 * - tonal     → accent tint with accent text. For the other steps a user
 *               can take on a screen that already has its primary: it reads
 *               as clickable from across the room without competing for
 *               "the" action. Never more than a handful per screen.
 * - secondary → default. Hairline border, surface fill.
 * - ghost     → in-table / in-panel utilities. Neutral at rest; the label
 *               takes the accent on hover so it reads as a link would.
 * Sizes: sm (28px) for dense rows, md (32px) everywhere else.
 */
export type ButtonVariant =
  | 'primary'
  | 'tonal'
  | 'secondary'
  | 'ghost'
  | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  loading?: boolean;
  /**
   * Why the control is unavailable. Setting it disables the button and puts
   * the reason in `title`. Rule: never ship a bare disabled button — pair
   * this with a visible sentence nearby (see DESIGN_SYSTEM "Blocked states").
   */
  disabledReason?: string;
}

const variantClass: Record<ButtonVariant, string> = {
  primary:
    'bg-v2-accent text-v2-on-accent hover:bg-v2-accent-hover disabled:hover:bg-v2-accent',
  tonal:
    'bg-v2-accent/[0.12] text-v2-accent hover:bg-v2-accent/[0.2] disabled:hover:bg-v2-accent/[0.12]',
  secondary:
    'bg-v2-surface text-v2-fg border border-v2-line-strong/80 hover:bg-v2-sunken disabled:hover:bg-v2-surface',
  ghost:
    'bg-transparent text-v2-fg-2 hover:bg-v2-accent/10 hover:text-v2-accent disabled:hover:bg-transparent disabled:hover:text-v2-fg-2',
  danger:
    'bg-v2-surface text-v2-danger border border-v2-danger/40 hover:bg-v2-danger/10',
};

const sizeClass: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-v2-12 gap-1.5 rounded-v2',
  md: 'h-8 px-3 text-v2-13 gap-2 rounded-v2',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      leading,
      trailing,
      loading = false,
      className,
      children,
      disabled,
      disabledReason,
      title,
      type = 'button',
      onClick,
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || !!disabledReason}
      title={disabledReason ?? title}
      aria-busy={loading || undefined}
      // While loading the button keeps focus (native `disabled` would drop it
      // to <body>, e.g. out of a dialog) but ignores activation.
      aria-disabled={loading || undefined}
      onClick={(e) => {
        if (loading) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick?.(e);
      }}
      className={clsx(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-semibold transition-colors duration-100',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
        variantClass[variant],
        sizeClass[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : leading}
      {children}
      {trailing}
    </button>
  ),
);
Button.displayName = 'Button';

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  size?: ButtonSize;
  variant?: 'ghost' | 'secondary';
  /** Same contract as `Button`: disables the control and explains why. */
  disabledReason?: string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      label,
      size = 'md',
      variant = 'ghost',
      className,
      children,
      disabled,
      disabledReason,
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={disabledReason ? `${label} — ${disabledReason}` : label}
      disabled={disabled || !!disabledReason}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-v2 transition-colors duration-100',
        'disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-7 w-7' : 'h-8 w-8',
        variant === 'ghost'
          ? 'text-v2-fg-3 hover:bg-v2-sunken hover:text-v2-fg'
          : 'border border-v2-line-strong/80 bg-v2-surface text-v2-fg-2 hover:bg-v2-sunken hover:text-v2-fg',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  ),
);
IconButton.displayName = 'IconButton';

/**
 * Chip: a small on/off toggle for quick picks — preset selections, filters
 * that apply together. State is `aria-pressed`, shown by border and text as
 * well as tint, never colour alone. Wrap a set in a `role="group"` with an
 * `aria-label`. An action that only ever fires (a "Clear") is a ghost
 * `Button`, not a chip.
 */
export const Chip: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { pressed: boolean }
> = ({ pressed, className, children, ...rest }) => (
  <button
    type="button"
    aria-pressed={pressed}
    className={clsx(
      'inline-flex h-7 select-none items-center rounded-v2 border px-2.5 text-v2-12 font-semibold transition-colors duration-100',
      pressed
        ? 'border-v2-accent/50 bg-v2-accent/[0.08] text-v2-accent'
        : 'border-v2-line-strong/80 bg-v2-surface text-v2-fg-2 hover:bg-v2-sunken hover:text-v2-fg',
      className,
    )}
    {...rest}
  >
    {children}
  </button>
);

export const Spinner: React.FC<{ className?: string }> = ({ className }) => (
  <span
    aria-hidden
    className={clsx(
      'inline-block h-3.5 w-3.5 animate-spin rounded-full border-[1.5px] border-current border-t-transparent',
      className,
    )}
  />
);
